import { SUBTYPE_OPTIONS } from '@cms/utils/constants';
import { mapLookupsToOptions } from '@cms/utils/helpers';
import { collectionsLookupsQuery } from '@cms/utils/queries';
import { useAppSelector } from '@cms/utils/store/hooks';
import { ISystemEvent } from '@dl/declarations';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormData, initialValues, schema } from '../../utils/forms/event';
import { EventsPath } from '../../utils/routes';
import { ControlledDropdown } from '../data/Dropdown';
import { ControlledAssetInput } from '../data/FileInput';
import { ControlledSystemTextInput } from '../data/TextInput';
import { FormContainer } from './FormContainer';

const Form = () => {
    const { setValue, watch } = useFormContext<FormData>();

    const { entity } = useAppSelector((state) => state.form)
    const [selected_subtype] = watch(['subtype']);

    const { data: collections = [] } = collectionsLookupsQuery({
        subtype: selected_subtype || '',
        collection_type: 'Event'
    }, {
        enabled: !!selected_subtype
    })

    useEffect(() => {
        setValue('collections', ((entity as ISystemEvent)?.collectionsObjects || []).map((item) => item.collection_ref))
    }, [entity])

    return (
        <div className='w-full flex flex-col items-stretch gap-4'>
            <div className='flex justify-between gap-4'>
                <div className='min-w-1/2 space-y-2'>
                    <ControlledDropdown
                        description='Subtype'
                        name='subtype'
                        options={SUBTYPE_OPTIONS}
                        label='Add a content type'
                        valueKey='_id'
                        inputSize='full'
                        searchOptions={{
                            allow: false
                        }}
                        allowSingleDeselect={false}
                    />

                    <ControlledDropdown
                        description='Collection'
                        name='collections'
                        options={mapLookupsToOptions(collections, 'name', () => 'Collections')}
                        label='Add to collections'
                        valueKey='_id'
                        inputSize='full'
                        searchOptions={{
                            allow: false
                        }}
                        allowMultiSelect
                        disabled={!selected_subtype}
                    />

                    <ControlledSystemTextInput
                        description='Title'
                        name='name'
                        inputSize='full'
                    />

                    <ControlledSystemTextInput
                        description='Description'
                        name='description'
                        inputSize='full'
                        multiline
                    />
                </div>

                <ControlledAssetInput
                    name="image"
                    mainAction={{
                        show: true,
                        actionText: "Event image"
                    }}
                    initialValue={(entity as ISystemEvent)?.imageObject || null}
                />
            </div>

            <ControlledSystemTextInput
                description='Type'
                name='type'
                inputSize='full'
            />

            <ControlledSystemTextInput
                description='URL'
                name='url'
                inputSize='full'
            />

            <div className='grid grid-cols-3 items-center gap-4'>
                <ControlledSystemTextInput
                    description='Start Date'
                    name='start_date'
                    type='date'
                />

                <ControlledSystemTextInput
                    description='End Date'
                    name='end_date'
                    type='date'
                />

                <ControlledSystemTextInput
                    description='Time'
                    name='time'
                    type='time'
                />
            </div>

            <div className='grid grid-cols-2 items-center gap-4'>
                <ControlledSystemTextInput
                    description='Region'
                    name='region'
                />

                <ControlledSystemTextInput
                    description='Country'
                    name='country'
                />

                <ControlledSystemTextInput
                    description='City'
                    name='city'
                />

                <ControlledSystemTextInput
                    description='Street'
                    name='street'
                />
            </div>
        </div>
    )
}

export const EventForm = () => {
    return (
        <FormContainer<FormData>
            title='Event'
            formSize='1/2'
            headerOptions={{
                show: false
            }}
            queryOptions={{
                enabled: true,
                basePath: EventsPath,
            }}
            schema={schema}
            defaultValues={initialValues}
            deleteOptions={{
                show: true
            }}
            shouldRedirectAfterSubmit={false}
        >
            <Form />
        </FormContainer>
    )
}